import { MouseEventHandler, ReactNode, useEffect, useState } from 'react';
import { omit } from 'lodash';
import classNames from 'classnames';

import { Select } from '@/components/form/select';
import { CheckboxWithLabel } from '@/components/form/checkbox';
import { Badge } from '@/components/badge';
import { Input } from '@/components/form/input';
import { Dropdown, DropdownMenuItem, DropdownIconSizePx } from '@/components/dropdown';
import { IconButton } from '@/components/button';
import { Icon } from '@/components/icon';
import { createBackNavigationToast, useToastContext } from '@/components/toast';
import { useTrackEvent } from '@/lib/analytics';
import { DateTimeInput } from '@/components/form/datetime-input';
import { timePrecisionOptions } from '@/explore/utils/grouping';
import { variableColors, variableAlternateColors } from '@/explore/components/variable-color';
import { HideInEmbedded } from '@/components/layout/hide-in-embedded';
import { useAccountTimezone } from '@/lib/accounts/context';
import { InteractableProps, shouldPropagateEvent } from '@/explore/utils/interactable';

import { useExplorationContext } from '../exploration-context';
import { useExplorationUIContext } from '../exploration-ui-context';
import {
  getVariableColor,
  replaceVariableDefinition,
  setVariableDefinitionDefaultValue,
} from '../../utils';
import {
  VariableCell,
  VariableDefinition,
  booleanParameter,
  dateRangeParameter,
  dateParameter,
  ExplorationParameter,
  numberParameter,
  stringParameter,
  timeIntervalParameter,
  arrayParameter,
} from '../../types';

import { useExplorationCellContext } from '../exploration-cell-context';

import { EnumInput } from './enum-input';
import { ArrayInput } from './array-input';

import styles from './variable-cell.module.scss';
import explorationStyles from '../exploration.module.scss';

interface VariableInputProps {
  label: string;
  children: ReactNode;
  variables: VariableDefinition[];
}

const VariableInput = ({ label, children, variables }: VariableInputProps) => (
  <div className={styles.container}>
    <Badge
      bgColor={getVariableColor(label, variables, variableAlternateColors)}
      textColor={getVariableColor(label, variables, variableColors)}
      iconName="Variable"
      label={label}
    />
    <div className={styles.input} {...InteractableProps}>
      {children}
    </div>
  </div>
);

interface TextInputProps {
  value: string;
  type: string;
  onChange: (value: string) => void;
}

// Wrapper component around Input to trigger changes only when user has stopped changing the value. It mimicks
// the behavior of a normal input field. In React, the onChange has different behavior and this wrapper is meant
// to provide a similar behavior.
const TextInput = ({ value, type, onChange }: TextInputProps) => {
  const [internalValue, setInternalValue] = useState(value);

  useEffect(() => setInternalValue(value), [value]);

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      triggerChange();
    }
  };

  const triggerChange = () => {
    if (value === internalValue) {
      return;
    }

    onChange(internalValue);
  };

  return (
    <Input
      value={internalValue}
      type={type}
      onBlur={triggerChange}
      onChange={(event) => setInternalValue(event.currentTarget.value)}
      onKeyDown={handleKeyUp}
    />
  );
};

interface StringInputProps {
  value: string;
  onChange: (value: string) => void;
}

const StringInput = ({ value, onChange }: StringInputProps) => (
  <TextInput value={value} type="text" onChange={onChange} />
);

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
}

const NumberInput = ({ value, onChange }: NumberInputProps) => (
  <TextInput
    value={value.toString()}
    type="number"
    onChange={(value) => onChange(parseFloat(value))}
  />
);

interface VariableCellViewProps {
  cell: VariableCell;
  variables: VariableDefinition[];
  onSelectCell?: () => void;
  onSetDraggable: (value: boolean) => void;
  disabled?: boolean;
}

export const VariableCellView = (props: VariableCellViewProps) => {
  const { cell, variables, disabled = false } = props;
  const { definition } = cell;

  const timezone = useAccountTimezone();
  const { isConversationPart } = useExplorationCellContext();
  const { exploration, deleteCell, setParameter, getParameter, setExploration } =
    useExplorationContext();
  const { deselectCell } = useExplorationUIContext();
  const addToast = useToastContext();
  const trackEvent = useTrackEvent();
  const value = getParameter(definition.key) ?? '';

  const handleChange = (value2: ExplorationParameter) => {
    trackEvent('Variable Value Changed', {
      explorationId: exploration.explorationId,
      name: exploration.name,
      definition,
      value: value2,
    });
    setParameter(definition.key, value2);
  };

  const handleSetDefaultValue = (value: ExplorationParameter) =>
    setExploration(
      replaceVariableDefinition(
        setVariableDefinitionDefaultValue(definition, value),
        exploration,
        cell.id,
      ),
    );

  const handleDeleteCell = () => {
    deleteCell(cell.id);
    deselectCell();

    addToast(
      createBackNavigationToast(
        'Block Deleted',
        "If you didn't mean to delete it, you can use your browser back button or",
        'click undo',
      ),
    );

    trackEvent('Exploration Cell Deleted', {
      explorationId: exploration.explorationId,
      name: exploration.name,
      cell,
    });
  };

  const handleCellClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (shouldPropagateEvent(event)) {
      props.onSelectCell?.();
    }
  };

  const renderDisabled = () => {
    if (definition.kind === 'string') {
      const safeValue = stringParameter.parse(value);
      return safeValue.length > 0 ? safeValue : '-';
    }

    if (definition.kind === 'number') {
      return numberParameter.parse(value);
    }

    if (definition.kind === 'enum') {
      return stringParameter.parse(value);
    }

    if (definition.kind === 'multiselect') {
      return arrayParameter.parse(value);
    }

    if (definition.kind === 'time_interval') {
      return timeIntervalParameter.parse(value);
    }

    if (definition.kind === 'date_range') {
      const safeValue = dateRangeParameter.parse(value);
      return `${safeValue.start} - ${safeValue.end}`;
    }

    if (definition.kind === 'boolean') {
      return booleanParameter.parse(value) ? 'True' : 'False';
    }

    if (definition.kind === 'date') {
      return dateParameter.parse(value);
    }

    return null;
  };

  return (
    <div className={styles.variableCell} onClick={handleCellClick}>
      {!isConversationPart && (
        <Icon
          name="DragHandle"
          size={10}
          className={classNames(explorationStyles.dragHandle, styles.dragHandle)}
          onMouseOver={() => props.onSetDraggable(true)}
          onMouseOut={() => props.onSetDraggable(false)}
        />
      )}
      <div className={styles.menu} {...InteractableProps}>
        <HideInEmbedded>
          <Dropdown
            align="right"
            trigger={(isOpen, setIsOpen) => (
              <IconButton
                icon="MoreHorizontal"
                size="small"
                title="More..."
                type="gray"
                onClick={() => setIsOpen(!isOpen)}
              />
            )}
            items={[
              {
                label: 'Delete variable',
                icon: <Icon name="Trash2" size={DropdownIconSizePx} />,
                onClick: handleDeleteCell,
                sort: 10,
              },
              {
                label: 'Set value as default',
                icon: <Icon name="Bookmark" size={DropdownIconSizePx} />,
                onClick: () => handleSetDefaultValue(value),
                sort: 20,
              },
            ]
              .sort((a, b) => a.sort - b.sort)
              .map((item) => omit(item, ['sort']) as DropdownMenuItem)}
          />
        </HideInEmbedded>
      </div>

      <VariableInput key={definition.key} label={definition.key} variables={variables}>
        {disabled ? (
          renderDisabled()
        ) : definition.kind === 'string' ? (
          <StringInput
            value={stringParameter.parse(value)}
            onChange={(value) => handleChange(value)}
          />
        ) : definition.kind === 'number' ? (
          <NumberInput
            value={numberParameter.parse(value)}
            onChange={(value) => handleChange(value)}
          />
        ) : definition.kind === 'enum' ? (
          <EnumInput
            variable={definition}
            value={stringParameter.or(numberParameter).catch('').parse(value)}
            onChange={handleChange}
          />
        ) : definition.kind === 'multiselect' ? (
          <ArrayInput
            variable={definition}
            value={arrayParameter.catch([]).parse(value)}
            onChange={handleChange}
          />
        ) : definition.kind === 'time_interval' ? (
          <Select
            value={timeIntervalParameter.parse(value)}
            onChange={(value) => handleChange(value)}
            options={timePrecisionOptions}
          />
        ) : definition.kind === 'boolean' ? (
          <CheckboxWithLabel
            checked={booleanParameter.parse(Boolean(value))}
            onChange={(value) => handleChange(value.checked)}
            id={definition.key}>
            {definition.key}
          </CheckboxWithLabel>
        ) : definition.kind === 'date' ? (
          <DateTimeInput
            mode="single"
            key={definition.key}
            value={{ from: dateParameter.parse(value), to: dateParameter.parse(value) }}
            timezone={timezone}
            onChange={(range) => handleChange(range.from ?? '')}
          />
        ) : null}
      </VariableInput>
    </div>
  );
};
